import { call, put, select, race } from 'redux-saga/effects';
import isEqual from 'lodash/isEqual';
import * as api from '../../../../../util/api/RestaurantApiClient';
import resolveDeliveryAddress from '../../../../../util/deliveryAddressService';
import { formatCartForApi } from '../../../../../util/Helpers';
import { getTimeParams } from '../../../../../util/Restaurant';
import { createCheckoutAttemptContext } from '../../../../../util/checkoutContext';
import {
  hasDeliveryRevision,
  sameAcknowledgedDestination,
  paymentResolutionBlocksMutation,
  checkoutRequiresResolution,
} from '../../../../../util/checkoutDeliveryAuthority';
import * as order from '../../../reducers/ducks/legacy/OrderDuck';
import { setSelectedAddress } from '../../../reducers/ducks/new/Address/AddressReducer';
import watchCheckoutContext from './watchCheckoutContext';
import { readCheckoutIssuance } from '../../../../../util/checkoutIssuanceRecovery';
import {
  checkoutStatusError,
  checkoutDispatchFailure,
} from '../../../../../util/checkoutDispatchFailure';
import {
  readCheckoutRecovery,
  persistCheckoutRecovery,
} from '../../../../../util/checkoutIdentityRecovery';

const failure = () =>
  new Error(
    'We could not confirm your latest delivery details. Your edits are kept. Please retry checkout.'
  );
let synchronizationSequence = 0;

function* synchronizeAttempt(context, key, isCurrent, resolution) {
  const initial = yield select((state) => state);
  const checkout = initial.order.checkoutData;
  const client = initial.restaurant.selectedRestaurantId;
  const id = checkout?.id;
  if (!id || !initial.main.user?.id) throw failure();
  const current = function* current() {
    if (
      (yield select(context)) !== key ||
      (yield select((state) => state.order.checkoutData?.id)) !== id
    )
      throw failure();
  };
  const delivery = initial.order.type?.value === 'delivery';
  let resolved =
    resolution || (yield call(api.resolveCheckout, { client, checkout: id }));
  yield call(current);
  if (resolved?.checkout_id !== id) throw failure();
  if (readCheckoutIssuance(initial))
    return { ...resolved, issuance_uncertain: true };
  if (paymentResolutionBlocksMutation(resolved))
    return { ...resolved, mutation_blocked: true };
  let address = initial.address.selectedAddress;
  let data = {
    ...getTimeParams(initial.time.selectedTime),
    mode: initial.order.type.value,
    ...(delivery ? { address: address?.address_id } : {}),
    cart: formatCartForApi(initial.order.cartItems),
  };
  const savedMatches = () =>
    (delivery
      ? sameAcknowledgedDestination(address, resolved.saved_destination)
      : resolved.saved_destination == null) &&
    Object.keys(data).every((field) =>
      isEqual(resolved.saved_cart?.[field], data[field])
    );
  if (!resolved.saved_delivery_available || !savedMatches()) {
    if (delivery) {
      address = yield call(
        resolveDeliveryAddress,
        initial.address.selectedAddress,
        initial.main.user.id,
        isCurrent,
        { saveDetails: true }
      );
      yield call(current);
      yield put(setSelectedAddress(address));
      yield call(current);
      data = { ...data, address: address.address_id };
    }
    if (
      !Object.keys(data).every((field) =>
        isEqual(resolved.saved_cart?.[field], data[field])
      )
    ) {
      yield call(api.setOrderCart, { client, data });
      yield call(current);
    }
    resolved = yield call(api.resolveCheckout, { client, checkout: id });
    yield call(current);
    if (resolved?.checkout_id !== id) throw failure();
    if (paymentResolutionBlocksMutation(resolved))
      return { ...resolved, mutation_blocked: true };
  }
  if (
    !resolved.saved_delivery_available ||
    !hasDeliveryRevision(resolved.saved_delivery_binding) ||
    !savedMatches()
  )
    throw failure();
  const acknowledged = yield call(api.synchronizeCheckout, {
    client,
    checkout: id,
    expected_delivery_revision: resolved.delivery_revision ?? null,
    saved_delivery_binding: resolved.saved_delivery_binding,
  });
  yield call(current);
  if (acknowledged?.checkout_id !== id) throw failure();
  if (
    acknowledged.already_confirmed ||
    ['processing', 'succeeded', 'already_confirmed'].includes(
      acknowledged.state
    )
  )
    return acknowledged;
  if (
    acknowledged.state !== 'unpaid' ||
    acknowledged.replacement_allowed !== false ||
    acknowledged.saved_delivery_binding !== resolved.saved_delivery_binding ||
    !hasDeliveryRevision(acknowledged.delivery_revision) ||
    (delivery
      ? !sameAcknowledgedDestination(address, acknowledged.destination)
      : acknowledged.destination != null) ||
    !Number.isFinite(acknowledged.totals?.total)
  )
    throw failure();
  yield put(
    order.setCheckoutData({
      ...checkout,
      ...acknowledged.totals,
      id,
      delivery_revision: acknowledged.delivery_revision,
    })
  );
  yield put(order.setOrderFees(acknowledged.totals.fees || []));
  yield put(
    order.setCheckoutDeliveryBinding({
      id,
      context: key,
      destination:
        initial.address.intentAnchor || initial.address.selectedAddress,
      revision: acknowledged.delivery_revision,
    })
  );
  return acknowledged;
}

// One bounded save -> resolve -> synchronize operation. No automatic retries,
// replacement checkout, payment creation or fallback is authorized here.
export default function* synchronizeDeliveryCheckout(options = {}) {
  const state = yield select((value) => value);
  if (
    state.order.type?.value !== 'delivery' &&
    !options.allowTakeout &&
    !checkoutRequiresResolution(state)
  )
    return true;
  const context = createCheckoutAttemptContext(state);
  const key = context(state);
  synchronizationSequence += 1;
  const operationId = synchronizationSequence;
  const ownsResult = (current) =>
    current.order.type?.value === state.order.type?.value &&
    current.restaurant.selectedRestaurantId ===
      state.restaurant.selectedRestaurantId &&
    current.order.checkoutData?.id === state.order.checkoutData?.id &&
    current.main.user?.id === state.main.user?.id &&
    current.order.checkoutDeliveryResult?.operationId === operationId;
  let active = true;
  let settled = false;
  yield put(order.setCheckoutDeliveryBinding(null));
  yield put(
    order.setCheckoutDeliveryResult({
      status: 'pending',
      operationId,
      userId: state.main.user?.id,
    })
  );
  try {
    const result = yield race({
      completed: call(
        synchronizeAttempt,
        context,
        key,
        () => active,
        options.resolution
      ),
      changed: call(watchCheckoutContext, key, context),
    });
    if (result.changed) throw failure();
    const response = result.completed;
    const status =
      response.state === 'unpaid' &&
      !response.issuance_uncertain &&
      !response.mutation_blocked
        ? 'success'
        : 'blocked';
    if (!(yield select(ownsResult))) return false;
    yield put(
      order.setCheckoutDeliveryResult({ status, resolution: response })
    );
    settled = true;
    return status === 'success';
  } catch (error) {
    if (yield select(ownsResult)) {
      const diagnostic = checkoutDispatchFailure(error);
      const record = yield select(readCheckoutRecovery);
      if (diagnostic && record) {
        const retained = { ...record, diagnostic };
        yield call(persistCheckoutRecovery, retained);
        yield put(order.setCheckoutRecovery(retained));
      }
      yield put(
        order.setCheckoutDeliveryResult({
          status: 'error',
          message: checkoutStatusError(error, failure().message).message,
        })
      );
    }
    settled = true;
    return false;
  } finally {
    active = false;
    if (!settled && (yield select(ownsResult)))
      yield put(
        order.setCheckoutDeliveryResult({
          status: 'error',
          message: failure().message,
        })
      );
  }
}
